import i18n from '@/util/config/i18n'
import { isExportMode } from '@/util/canvas-render-mode'
import { trackExportTask } from '@/util/export-readiness'
import { EmbedProviderId, embedProvider, toSafeEmbedSrc } from '@/util/embed-click-to-load'
import { embedUrlToWatchUrl } from '@/util/embed-link-parser'
import { EmbedType } from '@/modules/elements/models/Embed'
import type Element from '@/modules/elements/models/Element'
import { RootEntityType } from '@/modules/elements/models/Element'

/**
 * Print cards for embeds in the PDF export (canvas load 1.6): a QR code and the link, the way InteractivePrintPlaceholder
 * shows interactive embed elements. IFramely embeds and rich-text iframes (the iframe node and static content) show
 * one too, whatever `static_first_render` says, so the export loads no player and waits for none. The markup here matches
 * InteractivePrintPlaceholder.vue, and both are styled by the `notch-print-placeholder` mixin.
 */

export const PRINT_CARD_CLASS = 'interactive-print-placeholder'
const QR_URL_ATTR = 'data-print-qr'
const QR_CODE_SIZE = 200
const QR_CODE_MARGIN = 2
// Players whose page a reader should open instead of the bare player, as embed elements link it
const WATCH_PAGE_TYPES: Partial<Record<EmbedProviderId, EmbedType>> = {
  youtube: EmbedType.YOUTUBE,
  vimeo: EmbedType.VIDEO,
  loom: EmbedType.LOOM
}

// An allowlist: a URL with anything unusual before `://` (control characters, another scheme) is rejected
const LINK_URL = /^https?:\/\//i

/** The URL a print card may link and encode: http(s) only (`//host` becomes https); null for anything else. */
export function toPrintLinkUrl(url: string | null | undefined): string | null {
  const trimmed = url?.trim()
  if (!trimmed) return null
  const absolute = trimmed.startsWith('//') ? `https:${trimmed}` : trimmed
  return LINK_URL.test(absolute) ? absolute : null
}

/**
 * A link to an element in the app, for content that has no public URL (an uploaded file is private): the space being
 * exported, on the element's own page (the export prints every page, the URL names one at most), opened on the element.
 * The export query and fragment are dropped. Whoever scans it signs in as usual.
 */
export function printElementLink(element: Pick<Element, 'id' | 'rootEntityId' | 'rootEntityType'>): string | null {
  if (typeof window === 'undefined') return null
  const url = new URL(window.location.href)
  // The space's own path, as the URL has it (slug, alias or custom domain), without the page or tab after it
  const spacePath = url.pathname.replace(/\/(pages\/[^/]+|activities)\/?$/, '').replace(/\/$/, '')
  url.pathname =
    element.rootEntityType === RootEntityType.SPACE_PAGE && element.rootEntityId
      ? `${spacePath}/pages/${encodeURIComponent(element.rootEntityId)}`
      : spacePath || '/'
  url.search = ''
  url.hash = ''
  url.searchParams.set('element', element.id)
  return toPrintLinkUrl(url.href)
}

/** "DOCX" for "Brief.docx"; empty when the name has no extension. The label under a file card's name. */
export function printFileType(fileName: string | null | undefined): string {
  const name = fileName || ''
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot + 1).toUpperCase() : ''
}

/**
 * What a file card's QR code opens: an external file's own URL; an uploaded one is private, so the element in the app
 * (the scanner signs in, and the app serves the file as it does now). Empty when neither exists.
 */
export function printFileUrl(
  file: { isExternalFile?: boolean; url?: string | null } | null | undefined,
  element: Pick<Element, 'id' | 'rootEntityId' | 'rootEntityType'> | undefined
): string {
  return (file?.isExternalFile && file.url) || (element && printElementLink(element)) || ''
}

/** Embeds that are not embed elements show a print card whenever exporting, as an iframe can stall the PDF. */
export function isEmbedPrintCardEnabled(): boolean {
  return isExportMode()
}

/**
 * The QR code for `url` as a data URL; the export waits for it. Null for a URL no QR code can hold (too long): the
 * card still shows the link. Failing to load the QR library fails the export, as its cards would print without codes.
 */
export function printQrCodeDataUrl(url: string): Promise<string | null> {
  const code = import('qrcode').then(QRCode =>
    QRCode.toDataURL(url, { width: QR_CODE_SIZE, margin: QR_CODE_MARGIN }).catch(() => null)
  )
  return trackExportTask('qr-code', code)
}

/**
 * The print card for an embed loading `src`, linking the video's page for a known player, without its QR code (added by hydratePrintCards, as generating it is
 * asynchronous). A src an embed may not load (see toSafeEmbedSrc) gets an empty box: the export shows no link to it.
 */
export function createEmbedPrintCard(doc: Document, src: string | null | undefined): HTMLElement {
  const safeSrc = toSafeEmbedSrc(src)
  const card = doc.createElement('div')
  if (!safeSrc) return card
  const provider = embedProvider(safeSrc)
  const watchType = WATCH_PAGE_TYPES[provider.id]
  const pageUrl = watchType ? embedUrlToWatchUrl(safeSrc, watchType) : safeSrc
  card.className = PRINT_CARD_CLASS
  card.setAttribute(QR_URL_ATTR, pageUrl)

  const content = child(card, 'div', `${PRINT_CARD_CLASS}__content`)
  const qrBox = child(content, 'div', `${PRINT_CARD_CLASS}__qr-box`)
  child(qrBox, 'div', `${PRINT_CARD_CLASS}__qr-label`).textContent = i18n.t(
    'embeds.printPlaceholder.scanToView'
  ) as string
  const info = child(content, 'div', `${PRINT_CARD_CLASS}__info`)
  if (provider.label) child(info, 'div', `${PRINT_CARD_CLASS}__provider`).textContent = provider.label
  const url = child(info, 'div', `${PRINT_CARD_CLASS}__url`)
  child(url, 'span', `${PRINT_CARD_CLASS}__url-label`).textContent = i18n.t(
    'embeds.printPlaceholder.viewOnline'
  ) as string
  const link = child(url, 'a', `${PRINT_CARD_CLASS}__url-link`)
  link.setAttribute('href', pageUrl)
  link.setAttribute('target', '_blank')
  link.setAttribute('rel', 'noopener noreferrer')
  link.textContent = pageUrl
  return card
}

function child<K extends keyof HTMLElementTagNameMap>(
  parent: HTMLElement,
  tag: K,
  className: string
): HTMLElementTagNameMap[K] {
  const el = parent.ownerDocument.createElement(tag)
  el.className = className
  parent.appendChild(el)
  return el
}

/** Adds the QR code to each print card under `root` that has none yet. A code that fails leaves the card without one. */
export function hydratePrintCards(root: ParentNode): void {
  root.querySelectorAll<HTMLElement>(`.${PRINT_CARD_CLASS}[${QR_URL_ATTR}]`).forEach(card => {
    const url = card.getAttribute(QR_URL_ATTR) as string
    // Hydrating again while the code is generated must not add a second one
    card.removeAttribute(QR_URL_ATTR)
    const qrBox = card.querySelector(`.${PRINT_CARD_CLASS}__qr-box`)
    if (!qrBox) return
    printQrCodeDataUrl(url)
      .then(dataUrl => {
        if (!dataUrl) return
        const img = card.ownerDocument.createElement('img')
        img.alt = i18n.t('embeds.printPlaceholder.qrCodeAlt') as string
        img.decoding = 'async'
        img.src = dataUrl
        qrBox.appendChild(img)
      })
      // Already reported through export readiness
      .catch(() => undefined)
  })
}
